import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import axios from "axios";
import _ from "lodash";
import { ShiftType, UsersShiftsType } from "../../../Types/Entites/User";

// API endpoints matching your router
const API_BASE_URL = "userShift";

// Create user shift
export const createUserShiftApi = createAsyncThunk(
  "userShift/createUserShiftApi",
  async (data: UsersShiftsType, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/createUserShiftApi`,
        data,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        success: false,
        message:
          error.response?.data?.message ||
          "Erreur lors de la création du planning",
        action: error.response?.data?.action,
        data: error.response?.data?.data,
      });
    }
  },
);

// Find all user shifts
export const findUserShiftsApi = createAsyncThunk(
  "userShift/findUserShifts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/findUserShifts`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        success: false,
        message:
          error.response?.data?.message ||
          "Erreur lors du chargement des plannings",
      });
    }
  },
);

// Find all available shifts (Morning, Afternoon, Night, Flexible)
export const findShiftsApi = createAsyncThunk(
  "userShift/findShifts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/findShifts`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        success: false,
        message:
          error.response?.data?.message ||
          "Erreur lors du chargement des shifts",
      });
    }
  },
);
export const createShiftTemplateApi = createAsyncThunk(
  "userShift/createShiftTemplate",
  async (data: ShiftType, { rejectWithValue }) => {
    try {
      // You'll need to create this endpoint in your backend router
      const response = await axios.post(`userShift/createShift`, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        message: "Erreur lors de la création du shift",
      });
    }
  },
);
// Find user shift by user code
export const findUserShiftByUserCodeApi = createAsyncThunk(
  "userShift/findUserShiftByUserCode",
  async (userCode: string, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/findUserShiftByUserCode/${userCode}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        success: false,
        message: error.response?.data?.message || "Planning non trouvé",
      });
    }
  },
);

// Update user shift
export const updateUserShiftApi = createAsyncThunk(
  "userShift/updateUserShift",
  async (
    { userCode, data }: { userCode: string; data: Partial<UsersShiftsType> },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/updateUserShift/${userCode}`,
        data,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        success: false,
        message:
          error.response?.data?.message ||
          "Erreur lors de la mise à jour du planning",
      });
    }
  },
);

// Delete user shift (soft delete)
export const deleteUserShiftApi = createAsyncThunk(
  "userShift/deleteUserShift",
  async (userCode: string, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/deleteUserShift/${userCode}`,
      );
      return { success: true, userCode, message: response.data.message };
    } catch (error: any) {
      return rejectWithValue({
        success: false,
        message:
          error.response?.data?.message ||
          "Erreur lors de la suppression du planning",
      });
    }
  },
);

// Upsert user shift (create or update)
export const upsertUserShiftApi = createAsyncThunk(
  "userShift/upsertUserShift",
  async (
    { userCode, data }: { userCode: string; data: Partial<UsersShiftsType> },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/upsertUserShift/${userCode}`,
        data,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        success: false,
        message:
          error.response?.data?.message ||
          "Erreur lors de l'enregistrement du planning",
      });
    }
  },
);

// Interface for the slice state
interface UserShifts {
  userShiftsList: UsersShiftsType[];
  userShiftsByUserCode: { [userCode: string]: UsersShiftsType };
  shiftsList: ShiftType[];
  loading: boolean;
  error: any;
}

const initialState: UserShifts = {
  userShiftsList: [],
  userShiftsByUserCode: {},
  shiftsList: [],
  loading: false,
  error: null,
};

export const userShiftSlice = createSlice({
  name: "userShift",
  initialState,
  reducers: {
    clearUserShifts: (state) => {
      state.userShiftsList = [];
      state.userShiftsByUserCode = {};
    },
    clearErrors: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Create user shift
    builder.addCase(createUserShiftApi.fulfilled, (state, action: any) => {
      const newShift = action.payload.data;
      state.userShiftsList.unshift(newShift);
      state.userShiftsByUserCode[newShift.user_code] = newShift;
    });
    builder.addCase(createUserShiftApi.rejected, (state, action: any) => {
      state.error = action.payload?.message || "Erreur lors de la création";
    });

    // Find all user shifts
    builder.addCase(findUserShiftsApi.fulfilled, (state, action: any) => {
      const grouped = _.keyBy(action.payload, "user_code");
      return {
        ...state,
        userShiftsList: action.payload,
        userShiftsByUserCode: grouped,
        loading: false,
      };
    });
    builder.addCase(findUserShiftsApi.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(findUserShiftsApi.rejected, (state, action: any) => {
      state.loading = false;
      state.error = action.payload?.message || "Erreur lors du chargement";
    });

    // Find all shifts (Morning, Afternoon, Night, Flexible)
    builder.addCase(findShiftsApi.fulfilled, (state, action: any) => {
      return {
        ...state,
        shiftsList: action.payload,
        loading: false,
      };
    });
    builder.addCase(findShiftsApi.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(findShiftsApi.rejected, (state, action: any) => {
      state.loading = false;
      state.error =
        action.payload?.message || "Erreur lors du chargement des shifts";
    });

    // Find user shift by user code
    builder.addCase(
      findUserShiftByUserCodeApi.fulfilled,
      (state, action: any) => {
        const userShift = action.payload;
        if (userShift) {
          state.userShiftsByUserCode[userShift.user_code] = userShift;
        }
      },
    );
    builder.addCase(createShiftTemplateApi.fulfilled, (state, action: any) => {
      state.shiftsList.push(action.payload.data);
    });
    builder.addCase(
      findUserShiftByUserCodeApi.rejected,
      (state, action: any) => {
        state.error = action.payload?.message;
      },
    );

    // Update user shift
    builder.addCase(updateUserShiftApi.fulfilled, (state, action: any) => {
      const updatedShift = action.payload.data;
      const index = state.userShiftsList.findIndex(
        (s: any) => s.user_code === updatedShift.user_code,
      );
      if (index !== -1) {
        state.userShiftsList[index] = updatedShift;
      }
      state.userShiftsByUserCode[updatedShift.user_code] = updatedShift;
    });
    builder.addCase(updateUserShiftApi.rejected, (state, action: any) => {
      state.error = action.payload?.message || "Erreur lors de la mise à jour";
    });

    // Delete user shift
    builder.addCase(deleteUserShiftApi.fulfilled, (state, action: any) => {
      const { userCode } = action.payload;
      state.userShiftsList = state.userShiftsList.filter(
        (s: any) => s.user_code !== userCode,
      );
      delete state.userShiftsByUserCode[userCode];
    });
    builder.addCase(deleteUserShiftApi.rejected, (state, action: any) => {
      state.error = action.payload?.message || "Erreur lors de la suppression";
    });

    // Upsert user shift
    builder.addCase(upsertUserShiftApi.fulfilled, (state, action: any) => {
      const upsertedShift = action.payload; // Assuming payload is the full object
      const index = state.userShiftsList.findIndex(
        (s: any) => s.user_code === upsertedShift.user_code,
      );

      if (index !== -1) {
        state.userShiftsList[index] = upsertedShift;
      } else {
        state.userShiftsList.unshift(upsertedShift);
      }
      state.userShiftsByUserCode[upsertedShift.user_code] = upsertedShift;
    });
    builder.addCase(upsertUserShiftApi.rejected, (state, action: any) => {
      state.error =
        action.payload?.message || "Erreur lors de l'enregistrement";
    });
  },
});

// Export actions
export const { clearUserShifts, clearErrors } = userShiftSlice.actions;

// Selectors (like your ListUsers, ListRoles, etc.)
export const ListUserShifts = (state: RootState) =>
  state.userShift.userShiftsList;
export const ListUserShiftsByUserCode = (state: RootState) =>
  state.userShift.userShiftsByUserCode;
export const ListShifts = (state: RootState) => state.userShift.shiftsList;
export const getUserShiftByUserCode = (state: RootState, userCode: string) =>
  state.userShift.userShiftsByUserCode[userCode];
export const getUserShiftsLoading = (state: RootState) =>
  state.userShift.loading;
export const getUserShiftsError = (state: RootState) => state.userShift.error;

export default userShiftSlice.reducer;
